/**
 * Notification Hooks
 *
 * Hooks for fetching and mutating notification data.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notificationsApi } from "@/api/services";
import type {
  NotificationConfig,
  NotificationConfigCreate,
  NotificationConfigUpdate,
  InAppNotification,
  InAppNotificationParams,
} from "@/api/types/notifications";

// ============================================================================
// In-App Notification Hooks
// ============================================================================

/**
 * Fetch unread notification count (for badge display)
 * No polling - fetches on mount and when refetch is called
 */
export function useUnreadCount() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => notificationsApi.getUnreadCount(),
    staleTime: 30 * 1000, // 30 seconds
  });

  return { data, loading: isLoading, error, refetch };
}

/**
 * Fetch notifications (daily summaries)
 * Note: API returns array directly
 */
export function useInAppNotifications(params?: InAppNotificationParams) {
  const { limit = 10, offset = 0, unread_only = false } = params || {};

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "list", { limit, offset, unread_only }],
    queryFn: () => notificationsApi.listNotifications({ limit, offset, unread_only }),
  });

  return { data: data || [], loading: isLoading, error, refetch };
}

/**
 * Fetch notifications with infinite pagination for scroll-based inbox UIs.
 */
export function useInfiniteInAppNotifications(
  params?: Omit<InAppNotificationParams, "offset">,
) {
  const { limit = 10, unread_only = false } = params || {};

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["notifications", "list", "infinite", { limit, unread_only }],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      notificationsApi.listNotifications({
        limit,
        offset: pageParam,
        unread_only,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < limit) {
        return undefined;
      }

      return allPages.length * limit;
    },
  });

  return {
    data: data?.pages.flatMap((page) => page) || [],
    loading: isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}

/**
 * Hook to fetch notification detail
 */
export function useNotificationDetail(dateId: string | null) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "detail", dateId],
    queryFn: () => notificationsApi.getNotificationDetail(dateId!),
    enabled: !!dateId,
  });

  return { 
    data, 
    loading: isLoading, 
    error,
    refetch 
  };
}

/**
 * Hook to mark a single notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (dateId: string) => notificationsApi.markAsRead(dateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return {
    execute: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook to mark ALL notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return {
    execute: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

// ============================================================================
// Notification Config Hooks
// ============================================================================

/**
 * Fetch discovery options for notification config form
 * Returns storm_impact_models (with weather_models and variables), geographic_hierarchies, and comparison_operators
 */
export function useNotificationSettingsDiscovery() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "discovery"],
    queryFn: () => notificationsApi.getDiscoveryOptions(),
    staleTime: Infinity,
  });

  return { data, loading: isLoading, error, refetch };
}

/**
 * Fetch user's notification configs
 * Note: API returns array directly
 */
export function useNotificationConfigs(params?: {
  geographic_node_id?: string;
  is_active?: boolean;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "configs", "list", params],
    queryFn: () => notificationsApi.listConfigs(params),
  });

  return { data: data || [], loading: isLoading, error, refetch };
}

/**
 * Hook to fetch a specific notification config (for editing)
 */
export function useGetNotificationConfig(id: string | null) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["notifications", "configs", "detail", id],
    queryFn: () => notificationsApi.getConfig(id!),
    enabled: !!id,
  });

  return { data, loading: isLoading, error, refetch };
}

/**
 * Hook to create a notification config
 */
export function useCreateNotificationConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (data: NotificationConfigCreate) => notificationsApi.createConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "configs"] });
    },
  });

  return {
    execute: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook to update a notification config
 */
export function useUpdateNotificationConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: NotificationConfigUpdate }) =>
      notificationsApi.updateConfig(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "configs"] });
    },
  });

  // Wrapper to match old signature (id, data) -> mutate({id, data})
  const execute = (id: string, data: NotificationConfigUpdate) => {
    return mutation.mutateAsync({ id, data });
  };

  return {
    execute,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Hook to delete a notification config
 * Note: Returns void (204 No Content)
 */
export function useDeleteNotificationConfig() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "configs"] });
    },
  });

  return {
    execute: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
